import { io } from 'socket.io-client';
import toast from './toastUtils';
import { tokenStorage } from './secureStorage';

// Base URL configuration with fallback and error handling
const getBaseUrl = () => {
  const useLocal = process.env.REACT_APP_USE_LOCAL === 'true';
  const isProduction = process.env.NODE_ENV === 'production';
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Production safeguard: Never use localhost in production builds
  if (isProduction && useLocal) {
    console.error('🚨 PRODUCTION SAFEGUARD: REACT_APP_USE_LOCAL=true detected in production build!');
    console.error('Forcing production socket URL to prevent localhost in production.');
    return 'https://backend-neoeloi-neoelois-projects.vercel.app';
  }
  
  // Use local development URL if flag is set OR if running on localhost
  if (useLocal || isLocalhost) {
    console.log('🔧 Using local development socket URL: http://localhost:5000');
    return 'http://localhost:5000';
  }
  
  // Use environment variable or fallback to production
  const url = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_BASE_URL?.replace('/api', '');
  if (!url) {
    console.warn('Socket URL not configured, falling back to production URL');
    return 'https://backend-neoeloi-neoelois-projects.vercel.app';
  }
  return url;
};

const SOCKET_BASE_URL = getBaseUrl();
const isVercelHosted = SOCKET_BASE_URL?.includes('.vercel.app');
const socketDisabled =
  process.env.REACT_APP_DISABLE_SOCKET === 'true' ||
  (isVercelHosted && process.env.REACT_APP_ENABLE_VERCEL_SOCKET !== 'true');

if (socketDisabled) {
  console.info(
    'Real-time socket connections are disabled for this environment.',
    { baseUrl: SOCKET_BASE_URL, isVercelHosted }
  );
}

// Initialize the Socket.IO client with enhanced options
const socket = socketDisabled
  ? null
  : io(SOCKET_BASE_URL, {
      autoConnect: false,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000, // 10 second timeout
    });

// Connection state
let isConnected = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// Event listeners for specific notification types
const notificationHandlers = {
  reportFeedback: [],
  // Add more handlers here as needed for other notification types
};

// Function to handle 'notification' events and dispatch to specific handlers
const handleNotification = (data) => {
  const { type, notification, _id } = data; // _id is present for 'deleted' type
  let title, message, reportId;

  if (notification) {
    ({ title, message, reportId } = notification);
  }

  switch (type) {
    case 'reportFeedback':
      // Display both the title and the full message including admin's response
      toast.success(
        <div>
          <strong>{title}</strong>
          <p>{message}</p>
        </div>
      );
      break;
    case 'deleted':
      // Handle deletion event
      toast.info('A notification has been deleted.');
      // Update your notifications state to remove the deleted notification
      if (window.setNotifications) { // Replace with your actual state management logic
        window.setNotifications((prevNotifications) =>
          prevNotifications.filter((notif) => notif._id !== _id)
        );
      }
      break;
    case 'created':
      // Handle creation event
      toast.info(message || 'You have a new notification.');
      break;
    default:
      toast.info(message || 'You have a new notification.');
  }

  if (notificationHandlers[type] && notification) {
    notificationHandlers[type].forEach((handler) => handler(notification));
  }
};

// Function to connect the socket
const STORAGE_TOKEN_KEY = 'secure_auth_token';

const connect = () => {
  if (!socket) {
    console.info('Socket connection skipped: client disabled or unavailable.');
    return false;
  }
  const token = tokenStorage.getToken();
  if (token) {
    socket.auth = { token };
    try {
      socket.connect();
      console.log('Attempting socket connection...');
      return true;
    } catch (err) {
      console.error('Socket connection invocation failed:', err);
      return false;
    }
  } else {
    console.warn('No token available for socket connection');
    return false;
  }
};

// Function to disconnect the socket
const disconnect = () => {
  if (!socket) {
    return;
  }
  if (isConnected) {
    socket.disconnect();
    isConnected = false;
    reconnectAttempts = 0;
    console.log('Socket disconnected');
  }
};

// Function to setup event listeners
const setupEventListeners = (
  handleNewNotification,
  handleConnect,
  handleConnectError,
  setNotifications // Pass setNotifications as a parameter
) => {
  if (!socket) {
    return false;
  }
  socket.on('connect', () => {
    isConnected = true;
    reconnectAttempts = 0;
    console.log('Socket connected successfully');
    handleConnect?.();

    // If the user is an admin, join the 'admins' room
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.role === 'admin') {
      socket.emit('joinAdmins');
      console.log('Admin joined the admins room.');
    }
  });

  socket.on('connect_error', (err) => {
    console.error('Socket Connection Error:', err.message);
    reconnectAttempts++;
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      toast.error(
        'Unable to establish real-time connection. Please check your internet connection and refresh the page.'
      );
      socket.disconnect();
    } else {
      toast.warning('Attempting to reconnect...');
    }
    handleConnectError?.(err);
  });

  socket.on('notification', (data) => {
    handleNotification(data);
    handleNewNotification?.(data);

    // Specific handling for deletion
    if (data.type === 'deleted' && data._id && setNotifications) {
      setNotifications((prevNotifications) =>
        prevNotifications.filter((notif) => notif._id !== data._id)
      );
    }
  });

  socket.on('disconnect', (reason) => {
    isConnected = false;
    console.log(`Socket disconnected: ${reason}`);

    if (reason === 'io server disconnect') {
      socket.connect(); // Reconnect if the server disconnected the client
    } else if (reason === 'transport close' || reason === 'ping timeout') {
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          console.log('Attempting to reconnect...');
          socket.connect();
        }, 1000 * (reconnectAttempts + 1)); // Exponential backoff
      }
    }
  });

  socket.on('error', (error) => {
    console.error('Socket error:', error);
    toast.error('Connection error occurred');
  });

  return true;
};

// Function to remove all event listeners
const removeEventListeners = () => {
  if (!socket) {
    return;
  }
  socket.off('connect');
  socket.off('connect_error');
  socket.off('notification');
  socket.off('disconnect');
  socket.off('error');
};

// Function to reinitialize the socket connection (e.g., on token change)
const reinitialize = () => {
  if (!socket) {
    return;
  }
  disconnect();
  removeEventListeners();
  const newToken = tokenStorage.getToken();
  if (newToken) {
    socket.auth = { token: newToken };
    connect();
  }
};

// Listen for token changes in localStorage and reinitialize the socket connection
if (socket) {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_TOKEN_KEY) {
      reinitialize();
    }
  });
}

// Add connection status check
const isSocketConnected = () => Boolean(socket && isConnected);

/**
 * Register a handler for a specific notification type.
 * @param {string} type - The type of notification (e.g., 'reportFeedback').
 * @param {function} handler - The callback function to execute when the notification is received.
 */
const onNotification = (type, handler) => {
  if (notificationHandlers[type]) {
    notificationHandlers[type].push(handler);
  } else {
    console.warn(`No handlers registered for notification type: ${type}`);
  }
};

/**
 * Remove a handler for a specific notification type.
 * @param {string} type - The type of notification.
 * @param {function} handler - The callback function to remove.
 */
const offNotification = (type, handler) => {
  if (notificationHandlers[type]) {
    notificationHandlers[type] = notificationHandlers[type].filter(
      (h) => h !== handler
    );
  }
};

// Expose the socket client with all functionalities (custom health check removed)
const socketClient = {
  socket,
  connect,
  disconnect,
  setupEventListeners,
  removeEventListeners,
  reinitialize,
  isSocketConnected,
  onNotification,
  offNotification,
};

export default socketClient;
